import {
  FileText,
  Users2,
  TrendingUp,
  CalendarCheck,
  CreditCard,
  FileSignature,
  Plug,
  Gift,
  MessageCircle,
  Lightbulb,
  Activity,
  Receipt,
  BellRing,
  type LucideIcon,
} from "lucide-react";

export interface SolucaoContent {
  slug: string;
  title: string;
  tagline: string;
  description: string;
  icon: LucideIcon;
  seoTitle: string;
  seoDescription: string;
  benefits: { title: string; description: string; wide?: boolean }[];
  howItWorks: string[];
  howItWorksVariants?: { label: string; steps: string[] }[];
}

export const solucoes: SolucaoContent[] = [
  {
    slug: "proposta-digital",
    title: "Proposta Digital",
    tagline: "Venda mais rápido",
    description:
      "Sua vitrine digital gera propostas personalizadas em 1 minuto, incluindo substituições, frete e cupons, e saiba em que etapa da jornada o seu cliente está, ao vivo.",
    icon: FileText,
    seoTitle: "Proposta Digital para Buffet — AmeUp",
    seoDescription:
      "Gere e envie orçamentos profissionais pelo WhatsApp, com cálculo automático de preço, sazonalidade e taxas.",
    benefits: [
      { title: "Proposta personalizada em 1 minuto ", description: "Cardápio já cadastrado te ajuda a confeccionar a proposta em 1 clique" },
      { title: "Preço correto sempre", description: "Substituições, inclusões, taxas e cupons aplicados na hora." },
      { title: "Compartilhamento rápido", description: "Link enviado por WhatsApp em 1 clique." },
      { title: "Cliente autoatendido", description: "Ele pode simular quantas vezes quiser e você consegue visualizar em tempo real o funil." },
      { title: "Atendimento 24x7", description: "Permita que o seu cliente simule enquanto você está no evento ou descansando." },
      { title: "Visão 360º dos eventos", description: "Acompanhe cada proposta, simulação e etapa da jornada em um só painel." },
    ],
    howItWorks: [
      "Você monta a proposta ou pode deixar disponível para o cliente simular — Você que escolhe",
      "Sistema calcula preço final com todas as regras",
      "Proposta enviada e recebida por WhatsApp",
      "Você acompanha pelo CRM até o fechamento",
    ],
    howItWorksVariants: [
      {
        label: "Você monta a proposta",
        steps: [
          "Você monta a proposta no painel\u00a0",
          "Sistema calcula preço final com todas as regras do seu negócio",
          "Link da proposta gerado e enviado por WhatsApp",
          "Cliente recebe e aprova direto no celular",
          "Você acompanha pelo CRM até o fechamento",
        ],
      },
      {
        label: "Cliente simula sozinho",
        steps: [
          "Cliente acessa sua vitrine e simula o evento sozinho",
          "Sistema calcula preço final em tempo real",
          "Você visualiza a proposta e o estágio da venda no CRM\u00a0",
          "Seu time de vendas entra em contato com o cliente",
        ],
      },
    ],
  },
  {
    slug: "crm",
    title: "CRM",
    tagline: "O seu painel de vendas",
    description:
      "CRM feito para buffet: cada lead vira um card com histórico completo — mensagens, orçamentos, pagamentos, contrato. Nada se perde entre WhatsApp, planilha e caderno.",
    icon: Users2,
    seoTitle: "CRM para Buffet | AmeUp",
    seoDescription:
      "Centralize leads, mensagens, orçamentos e contratos do seu buffet em um único painel visual.",
    benefits: [
      { title: "Histórico unificado", description: "Toda comunicação com o cliente em um só card." },
      { title: "Automação de follow-up", description: "Lembretes automáticos por WhatsApp e e-mail." },
      { title: "Visão por status", description: "Kanban visual: novo, em negociação, ganho, perdido." },
      { title: "Zero lead esquecido", description: "Alertas quando um lead fica parado tempo demais." },
      { title: "Histórico completo dos seus clientes", description: "Todo o histórico de eventos, preferências e interações centralizado, pronto para campanhas, recompra e ações de vendas posteriores.", wide: true },
    ],
    howItWorks: [
      "Lead entra pela vitrine, formulário ou importação",
      "Card criado automaticamente no Kanban",
      "Toda interação (WhatsApp, orçamento, pagamento) fica anexada",
      "Automação move o card conforme o cliente avança",
    ],
  },
  {
    slug: "funil-de-vendas",
    title: "Funil de Vendas",
    tagline: "Visão do seu negócio",
    description:
      "Funil visual com métricas reais: quantos entraram na vitrine, quantos pediram orçamento, quantos fecharam. Descubra o gargalo e ataque o ponto certo.",
    icon: TrendingUp,
    seoTitle: "Funil de Vendas para Buffet — AmeUp",
    seoDescription:
      "Acompanhe a jornada do cliente do primeiro acesso ao contrato assinado, com métricas de conversão em tempo real.",
    benefits: [
      { title: "Métricas por etapa", description: "Taxa de conversão de cada passo do funil." },
      { title: "Diagnóstico rápido", description: "Descubra onde os leads estão sendo perdidos." },
      { title: "Comparação por período", description: "Meça o efeito de campanhas e promoções." },
      { title: "Metas visuais", description: "Defina objetivos mensais e acompanhe o avanço." },
    ],
    howItWorks: [
      "Sistema registra cada interação do lead automaticamente",
      "Funil é montado em tempo real com seus dados",
      "Alertas apontam onde a conversão caiu",
      "Você ajusta cardápio, preço ou abordagem",
    ],
  },
  {
    slug: "gestao-do-evento",
    title: "Gestão do Evento",
    tagline: "Seus eventos - 1 visão",
    description:
      "Ficha de evento completa: cardápio, convidados, endereço, equipe, pagamentos, contrato, alterações. Compartilhe com sua equipe e mantenha o cliente atualizado.",
    icon: CalendarCheck,
    seoTitle: "Gestão de Eventos para Buffet — AmeUp",
    seoDescription:
      "Gerencie cada evento do seu buffet do fechamento à execução: cardápio, convidados, equipe, pagamentos e contrato.",
    benefits: [
      { title: "Ficha unificada", description: "Cardápio, endereço, equipe e pagamentos num só lugar." },
      { title: "Alterações rastreadas", description: "Toda mudança gera alerta e cobrança automática." },
      { title: "Agenda inteligente", description: "Bloqueio de datas e sincronia com Google Calendar." },
      { title: "Portal do cliente", description: "Cliente acessa e acompanha o evento dele." },
    ],
    howItWorks: [
      "Evento criado automaticamente após fechamento",
      "Cardápio, pagamentos e contrato ficam vinculados",
      "Alterações do cliente disparam cobrança adicional",
      "Equipe acessa a ficha no dia do evento",
    ],
  },
  {
    slug: "pagamento-integrado",
    title: "Pagamento Integrado",
    tagline: "Chega de cobrança",
    description:
      "Integração nativa com PagBank e Stripe. Cliente paga na hora do fechamento ou em parcelas programadas. Split automático, comprovante e conciliação inclusos.",
    icon: CreditCard,
    seoTitle: "Pagamento Integrado para Buffet — AmeUp",
    seoDescription:
      "Receba pagamentos de eventos com Pix, cartão parcelado e boleto — tudo dentro da plataforma AmeUp.",
    benefits: [
      { title: "Pix, cartão e parcelamento", description: "Cliente escolhe como quer pagar." },
      { title: "Split automático", description: "Valor cai direto na sua conta bancária." },
      { title: "Conciliação automática", description: "Cada pagamento é vinculado ao evento certo." },
      { title: "Cobrança recorrente", description: "Parcelas mensais programadas até o evento." },
    ],
    howItWorks: [
      "Configure sua conta PagBank/Stripe em minutos",
      "Cliente paga direto no link do orçamento",
      "Sistema divide entrada + parcelas até o evento",
      "Você acompanha tudo no painel financeiro",
    ],
  },
  {
    slug: "contrato-automatico",
    title: "Contrato Automático",
    tagline: "Automatize o processo",
    description:
      "Modelo de contrato personalizado com os dados do evento preenchidos automaticamente. Cliente assina no celular, com selfie e validação — vale como assinatura eletrônica.",
    icon: FileSignature,
    seoTitle: "Contrato Automático para Buffet — AmeUp",
    seoDescription:
      "Contrato de prestação de serviços gerado e assinado digitalmente com validade jurídica, sem impressão ou cartório.",
    benefits: [
      { title: "Preenchimento automático", description: "Dados do evento já entram no contrato." },
      { title: "Assinatura no celular", description: "Cliente assina de onde estiver." },
      { title: "Validade jurídica", description: "Assinatura eletrônica com selfie e IP registrados." },
      { title: "Cláusulas customizáveis", description: "Adapte o modelo ao seu buffet." },
    ],
    howItWorks: [
      "Modelo de contrato configurado uma vez",
      "Sistema preenche automaticamente com dados do evento",
      "Cliente recebe link e assina no celular",
      "PDF assinado fica salvo na ficha do evento",
    ],
  },
  {
    slug: "integracoes",
    title: "Integrações",
    tagline: "Ferramentas que você utiliza em 1 só lugar",
    description:
      "Ecossistema aberto: WhatsApp, Google Calendar, Google Maps, PagBank, Stripe, Gemini, Claude e OpenAI. Tudo funcionando em conjunto para automatizar seu buffet.",
    icon: Plug,
    seoTitle: "Integrações AmeUp — WhatsApp, Google e IA",
    seoDescription:
      "Plataforma integrada com WhatsApp, Google Calendar, PagBank, Stripe e as principais IAs do mercado.",
    benefits: [
      { title: "WhatsApp nativo", description: "Envio de orçamentos, contratos e feedback pela sua linha." },
      { title: "Google Calendar", description: "Agenda de eventos sincronizada automaticamente." },
      { title: "Bancos e adquirentes", description: "PagBank e Stripe integrados para receber direto." },
      { title: "IAs de mercado", description: "Gemini, Claude e OpenAI apoiam cardápio e propostas." },
    ],
    howItWorks: [
      "Conecte cada serviço em minutos pelo painel",
      "Integração roda em background, sem código",
      "Você acompanha o status de cada conexão",
      "Suporte técnico para configurar tudo",
    ],
  },
  {
    slug: "programa-de-indicacao",
    title: "Programa de Indicação",
    tagline: "O cliente te apoiando",
    description:
      "Programa de indicação que te ajuda a vender mais: cada cliente recebe um link único, ganha recompensa por indicação convertida e você acompanha tudo em um Kanban dedicado.",
    icon: Gift,
    seoTitle: "Programa de Indicação para Buffet — AmeUp",
    seoDescription:
      "Sistema de indicações pronto para buffet: link único por cliente, recompensa automática e painel de acompanhamento.",
    benefits: [
      { title: "Link único por cliente", description: "Cada cliente ganha uma URL de indicação." },
      { title: "Recompensa automática", description: "Sistema aplica desconto ou bônus na conversão." },
      { title: "Kanban de indicações", description: "Acompanhe indicações do lead ao contrato assinado." },
      { title: "Cliente indicado é mais barato", description: "Custo de aquisição (CAC) de indicações pode ser até 70% menor do que leads pagos." },
      { title: "Conversão 3x maior", description: "Indicações convertem em média 3 a 5 vezes mais que canais tradicionais de marketing." },
      { title: "Compartilhamento fácil", description: "Cliente compartilha por WhatsApp em 1 toque." },
    ],
    howItWorks: [
      "Configure a recompensa (desconto, bônus ou serviço extra)",
      "Cliente recebe link único no portal dele",
      "Indicação convertida gera crédito automático",
      "Você acompanha o funil no Kanban",
    ],
  },
  {
    slug: "feedback-whatsapp",
    title: "Feedback WhatsApp",
    tagline: "Avalie o seu negócio com um clique",
    description:
      "Automação envia mensagem pelo WhatsApp 24h após o evento pedindo avaliação de 1 a 5 estrelas. Cliente responde na hora e você monta seu portfólio de reputação.",
    icon: MessageCircle,
    seoTitle: "Feedback via WhatsApp para Buffet — AmeUp",
    seoDescription:
      "Colete avaliações dos seus clientes automaticamente pelo WhatsApp e construa um portfólio de reputação.",
    benefits: [
      { title: "Envio automático", description: "Mensagem disparada no timing certo pós-evento." },
      { title: "Avaliação 1-5 estrelas", description: "Cliente responde direto pelo WhatsApp." },
      { title: "Agradecimento automático", description: "Sistema responde e registra o feedback." },
      { title: "Base para depoimentos", description: "Melhores avaliações viram vitrine na sua loja." },
    ],
    howItWorks: [
      "Evento é marcado como concluído",
      "24h depois, cliente recebe pedido de avaliação",
      "Resposta é salva e vinculada ao evento",
      "Você usa as melhores avaliações na sua vitrine",
    ],
  },
  {
    slug: "insight-de-vendas",
    title: "Insight de Vendas IA",
    tagline: "O seu consultor pessoal",
    description:
      "Painel de indicadores com ticket médio, cardápio mais vendido, sazonalidade, taxa de conversão e comparativo mês a mês. Decisões baseadas em dado, não em achismo.",
    icon: Lightbulb,
    seoTitle: "Insights de Vendas para Buffet — AmeUp",
    seoDescription:
      "Painel de indicadores: ticket médio, cardápio mais vendido, sazonalidade e taxa de conversão em tempo real.",
    benefits: [
      { title: "Ticket médio real", description: "Por período, cardápio e origem do lead." },
      { title: "Cardápio campeão", description: "Descubra qual pacote gera mais receita." },
      { title: "Sazonalidade clara", description: "Meses fortes e fracos com dados históricos." },
      { title: "Metas com progresso", description: "Objetivos financeiros com barra de avanço." },
    ],
    howItWorks: [
      "Sistema calcula indicadores automaticamente",
      "Painel atualizado em tempo real",
      "Comparativo mês/ano disponível",
      "Exporte relatórios em PDF quando quiser",
    ],
  },
  {
    slug: "monitoramento-ao-vivo",
    title: "Monitoramento ao Vivo",
    tagline: "Tempo real da sua empresa",
    description:
      "Dashboard operacional que mostra em tempo real: visitas na sua loja, orçamentos gerados, pagamentos recebidos, contratos assinados. Você comanda o buffet como uma central.",
    icon: Activity,
    seoTitle: "Monitoramento ao Vivo para Buffet — AmeUp",
    seoDescription:
      "Dashboard em tempo real com finanças, acessos, orçamentos e pagamentos do seu buffet, como uma central operacional.",
    benefits: [
      { title: "Acessos em tempo real", description: "Veja quantos visitantes estão na sua loja agora." },
      { title: "Financeiro ao vivo", description: "Entradas do dia, semana e mês em uma tela." },
      { title: "Alertas automáticos", description: "Notificação de novo lead, pagamento ou contrato." },
      { title: "Multi-dispositivo", description: "Acompanhe no celular, tablet ou desktop." },
    ],
    howItWorks: [
      "Sistema registra cada evento em tempo real",
      "Dashboard atualiza sem precisar recarregar",
      "Notificações push para eventos importantes",
      "Histórico completo para análise posterior",
    ],
  },
  {
    slug: "nota-fiscal",
    title: "Nota Fiscal",
    tagline: "Emita sem sair do pedido",
    description:
      "Emita a nota fiscal do evento ou pedido direto da ficha de venda. Os dados do cliente e do valor já vêm preenchidos, e você acompanha cada nota até a autorização, com PDF e XML salvos no sistema.",
    icon: Receipt,
    seoTitle: "Nota Fiscal para Buffet — AmeUp",
    seoDescription:
      "Emita NFS-e e notas de produto do seu buffet direto do pedido, com dados do cliente preenchidos e acompanhamento até a autorização.",
    benefits: [
      { title: "Emissão a partir do pedido", description: "Escolha o evento ou pedido e emita a nota sem digitar tudo de novo." },
      { title: "Dados já preenchidos", description: "Valor, descrição e CPF/CNPJ do cliente vêm do pedido e do contrato assinado." },
      { title: "Acompanhe cada nota", description: "Veja se está em processamento, autorizada ou recusada, e o motivo quando houver erro." },
      { title: "PDF e XML guardados", description: "Baixe os arquivos da nota a qualquer momento, junto da ficha do evento." },
      { title: "Várias atividades de serviço", description: "Cadastre as atividades da prefeitura e escolha a correta em cada emissão." },
      { title: "Menos retrabalho com a contabilidade", description: "As notas ficam organizadas por evento, prontas para consulta e conferência." },
    ],
    howItWorks: [
      "Você cadastra os dados fiscais da empresa uma única vez",
      "Escolhe o evento ou pedido e confere os dados já preenchidos",
      "A nota é enviada para emissão e você acompanha o status",
      "Autorizada, o PDF e o XML ficam salvos na ficha do evento",
    ],
  },
  {
    slug: "mensageria-automatica",
    title: "Mensageria Automática",
    tagline: "WhatsApp que trabalha por você",
    description:
      "Mensagens de WhatsApp disparadas sozinhas em cada etapa da venda: proposta parada, pagamento confirmado, parcela a vencer, pedido em preparo, saída para entrega e pedido de avaliação. Você edita os textos e o cliente sempre sabe o que está acontecendo.",
    icon: BellRing,
    seoTitle: "Mensageria Automática no WhatsApp para Buffet — AmeUp",
    seoDescription:
      "Automatize mensagens de WhatsApp para clientes do seu buffet: proposta sem resposta, pagamento, parcelas, entrega e feedback.",
    benefits: [
      { title: "Follow-up de proposta", description: "Cliente que não respondeu a proposta recebe um lembrete automático depois de 2 dias." },
      { title: "Confirmação de pagamento", description: "Assim que o pagamento cai, o cliente recebe a confirmação com data e horário." },
      { title: "Aviso de parcela a vencer", description: "Lembrete automático de cobrança nas parcelas programadas." },
      { title: "Acompanhamento da entrega", description: "Avisos de pedido em preparação e de saída para entrega, sem você precisar mandar." },
      { title: "Pedido de avaliação", description: "Depois do evento, o cliente recebe o convite para avaliar o seu serviço." },
      { title: "Textos do seu jeito", description: "Edite cada mensagem e ative só as automações que fazem sentido para o seu negócio." },
    ],
    howItWorks: [
      "Você conecta o WhatsApp do seu buffet e ativa as automações desejadas",
      "O sistema acompanha o status de cada evento e pedido",
      "Na hora certa, a mensagem é enviada ao cliente pelo seu número",
      "Cada envio fica registrado, sem mensagens repetidas",
    ],
  },
];

export const getSolucaoBySlug = (slug?: string) =>
  solucoes.find((s) => s.slug === slug);
